import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThread } from '../context/ThreadContext';
import { markAgentSeen } from '../utils/markAgentSeen';

/**
 * useStartAgentChat — single source of truth for "iniciar un chat" with an
 * agent (#401, option D: unified semantics across surfaces). Shared by the
 * sidebar agents list, the /explore grid (card click, hover preview, kebab)
 * and AgentDetailModal, so the same gesture always does the same thing:
 *
 *   1. Filter the sidebar thread list to this agent's threads (setAgentFilter),
 *      so the user sees their history with the agent and can resume any of it.
 *   2. Stamp the rolId for the next thread creation (setPendingRolId) —
 *      useMessageSending persists it as metadata.rolId, which drives the
 *      Broker direct-routing path (CLAUDE.md §15).
 *   3. Reset to the welcome screen (handleNewThread — also closes the mobile
 *      sidebar overlay via ThreadContext).
 *   4. Navigate to the chat route.
 *
 * No thread is created here: the real thread only exists once the user sends
 * the first message (useMessageSending.js), so starting an agent chat has no
 * DB side effects.
 *
 * @returns {(role: {id: string}) => void} start-chat handler; no-op without a role id
 */
export const useStartAgentChat = () => {
  const navigate = useNavigate();
  const { handleNewThread, setPendingRolId, setAgentFilter } = useThread();

  return useCallback((role) => {
    if (!role?.id) return;
    markAgentSeen(role);
    setAgentFilter(role.id);
    // Pass the role object too: navigating from /explore remounts Chat with an
    // empty useRoles() cache, and without this the welcome screen flashes the
    // generic brand header before the agent resolves.
    setPendingRolId(role.id, role);
    handleNewThread();
    navigate('/');
  }, [navigate, setAgentFilter, setPendingRolId, handleNewThread]);
};

export default useStartAgentChat;
